// import config from '_config';
import * as Screens from '_screens';
import _ from 'lodash/fp/lang';
import React from 'react';
import { connect } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { Account, App, Document } from '@aps-management/primapp-common';

import Router from '_config/router';
import apolloClient from '_utils/apolloClient';
import Firebase from '_utils/firebase';
import I18n from '_utils/i18n';
import masterClient from '_utils/masterClient';

import { PublicBookingProvider } from './screens/Public/PublicBooking/services/publicBookingService';
import Manifest from './utils/manifest';

/*
 * Sentinelle de la garde `derniereDemande`, pour l'utilisateur authentifié qui n'a pas
 * encore de compte. Un objet dédié plutôt qu'une chaîne : il ne peut entrer en
 * collision avec aucune demande réelle, quelle que soit la valeur des données Firebase.
 */
const SANS_COMPTE = Object.freeze({ sansCompte: true });

/* */
class Bootstrap extends React.PureComponent {
  /* */
  constructor(props) {
    super(props);

    this.slug = null;
    /*
     * Garde d'idempotence du chargement du compte. `null` = rien de demandé.
     * Voir le motif complet dans `componentDidUpdate`.
     */
    this.derniereDemande = null;

    Firebase.init();
    Firebase.connection();
  }

  /* */
  componentDidMount() {
    const { slug } = this.props.match.params;
    this.slug = slug;

    // Load APP GOLF
    if (this.slug) {
      this.props.loadGolf(apolloClient, { id: this.slug }).then((g) => {
        if (g) {
          I18n.locale = g.locale;
          global.origin = `${g.slug}`.toUpperCase();
        } else {
          this.props.noGolf();
        }
      });
    } else {
      this.props.noGolf();
      this.props.noAccount();
    }
  }

  /* */
  componentDidUpdate(prevProps) {
    const { user, golf } = this.props;

    document.title = (golf && golf.name) || 'prima.golf';

    /*
     * DÉCLENCHER SUR L'ÉTAT ATTEINT, JAMAIS SUR LA TRANSITION OBSERVÉE.
     *
     * La condition précédente était `golf && user && !isEqual(user, prevProps.user)` :
     * elle exigeait que `user` CHANGE à un instant où `golf` était DÉJÀ chargé. Or les
     * deux arrivent en parallèle et indépendamment : `Firebase.connection()` part du
     * constructeur, `loadGolf` seulement du `componentDidMount`.
     *
     * Au RECHARGEMENT, la session Firebase est restaurée depuis le cache local
     * (mesuré : 146 ms + 236 ms) avant que `GetGolf` ne réponde (1,93 s) :
     *
     *   1. `user` change, mais `golf` vaut encore `null`      -> condition fausse
     *   2. `golf` arrive, mais `user` n'a plus changé          -> condition fausse
     *
     * Plus rien ne redéclenchait, `account` restait `null`, donc `loading` restait
     * `true` et l'écran `Loading` tournait indéfiniment. La branche de repli
     * `else if (!user ...)` ne pouvait pas rattraper : `user` est truthy.
     *
     * À la CONNEXION l'ordre est inverse et favorable, ce qui explique que seul le
     * rechargement était touché.
     *
     * `primapp-embed` porte le même code SANS la garde `golf &&` et n'a jamais eu le
     * défaut : c'est bien cette garde, ajoutée ici parce que `golf.id` est nécessaire à
     * `getDocumentAcceptance`, qui transformait un déclencheur simple en coïncidence.
     */
    if (golf && user) {
      // Load ACCOUNT
      if (user.accountId) {
        /*
         * Garde d'idempotence. Sans elle, la condition ci-dessus étant vraie à CHAQUE
         * rendu, le compte serait rechargé en boucle.
         *
         * Elle mémorise la DEMANDE (l'utilisateur entier et le golf), pas seulement
         * l'identifiant de compte, et la compare par valeur. Ce choix conserve la
         * sémantique de rafraîchissement de l'ancien déclencheur : toute modification
         * du nœud Firebase `users/{uid}` rechargeait le compte, et le fait toujours.
         * Une clé réduite à `accountId:golfId` aurait silencieusement perdu ces
         * rafraîchissements.
         *
         * Ni `account` ni `loading` n'entrent dans la comparaison, à dessein :
         * `chargerCompte` les modifie, les y inclure rouvrirait une boucle.
         */
        const demande = { user, golfId: golf.id };
        if (!_.isEqual(this.derniereDemande, demande)) {
          this.derniereDemande = demande;
          this.chargerCompte(user, golf);
        }
      } else if (this.derniereDemande !== SANS_COMPTE) {
        this.derniereDemande = SANS_COMPTE;
        this.props.noAccount();
      }
    } else if (!user && !_.isEqual(user, prevProps.user)) {
      // Déconnexion : rouvrir la garde pour qu'une reconnexion redéclenche.
      this.derniereDemande = null;
      this.props.noAccount();
    }

    // Reload golf after logout
    if (golf === null) {
      if (this.slug) {
        this.props.loadGolf(apolloClient, { id: this.slug }).then((g) => {
          I18n.locale = g.locale;
        });
      }
    }
  }

  /*
   * Chargement du compte, puis des deux compléments qui en dépendent.
   *
   * Extrait de `componentDidUpdate` pour que le déclencheur reste lisible, et surtout
   * pour porter le `.catch` final : sans lui, un rejet quelconque de la chaîne laissait
   * `account` à `null` et reproduisait le spinner infini par un autre chemin que la
   * course ci-dessus.
   */
  chargerCompte = (user, golf) => {
    /*
     * Le compte a-t-il été RÉELLEMENT obtenu ? Ce drapeau décide de ce que fait le
     * `.catch` final, et c'est tout son intérêt. Voir le motif complet là-bas.
     */
    let compteObtenu = false;

    return this.props
      .loadAccount(apolloClient, { id: user.accountId })
      .then(({ payload }) => {
        /*
         * Deux cas rendent ici un `payload` sans `id`, et tous deux sont terminaux :
         *  - la requête a échoué : `loadAccount` avale l'erreur et dispatche
         *    `NO_ACCOUNT` avec un MESSAGE texte en `payload` ;
         *  - la requête a réussi mais `getAccount` est nul : `loadAccount` dispatche
         *    `LOAD_ACCOUNT` avec un objet sans `id`.
         * Dans les deux cas le réducteur a déjà remis `loading` à faux, et poursuivre
         * enverrait un `accountId` indéfini à `getDocumentAcceptance`.
         */
        if (!payload || !payload.id) return null;
        compteObtenu = true;

        const { license } = payload.ffg || { license: null };
        let promiseLicence = Promise.resolve(null);
        if (license) {
          promiseLicence = Account.api.getHcpIndexFFG(apolloClient, {});
        }

        /*
         * `Promise.all` sur des promesses individuellement rattrapées, et NON
         * `Promise.allSettled` : `react-app-polyfill` charge `core-js` 2, qui n'a
         * aucun module `all-settled`, et sur IE11 il remplace même `Promise` par une
         * implémentation qui ne la porte pas non plus. Sur les cibles déclarées dans
         * `package.json`, `Promise.allSettled` est donc `undefined` et la ligne
         * jetterait un `TypeError` à chaque chargement. Ces deux compléments sont
         * facultatifs : leur échec ne doit jamais empêcher le compte de s'afficher.
         */
        return Promise.all([
          promiseLicence.catch(() => undefined),
          Document.api
            .getDocumentAcceptance(masterClient, { accountId: payload.id, golfId: golf.id })
            .catch(() => undefined),
        ]);
      })
      .then((resultats) => {
        if (!resultats) return null;
        const [ffg, documentAcceptance] = resultats;
        return this.props.updateAccount({
          ...(ffg && { ffg }),
          documentAcceptance,
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[bootstrap] chargerCompte', err && err.message);

        /*
         * NE RIEN FAIRE D'AUTRE SI LE COMPTE EST DÉJÀ EN STORE.
         *
         * Une première version rouvrait la garde et appelait `noAccount()` sans
         * condition. C'était une boucle : `noAccount()` fait passer `account` de
         * l'objet chargé à `false`, donc les props changent, donc `componentDidUpdate`
         * s'exécute, donc la garde rouverte redéclenche `chargerCompte` - une requête
         * `GetAccount` par tour, en `no-cache`, sans terminaison. Et au passage le
         * golfeur, pourtant identifié, était renvoyé sur `CreateAccount`.
         *
         * Le seul échec possible AVANT que le compte soit obtenu vient de
         * `loadAccount`, qui n'échoue jamais (il avale ses erreurs). Rouvrir la garde
         * dans ce cas ne peut donc pas boucler, et libère `loading`.
         */
        if (compteObtenu) return;

        this.derniereDemande = null;
        this.props.noAccount();
      });
  };

  /* */
  retry = () => {
    Firebase.disconnect();
    Firebase.connection();
  };

  /* */
  renderRoutes() {
    const { golf, user, error, account, loading } = this.props;

    if (error) {
      return <Screens.Public.Error error={error} retry={this.retry} />;
    }

    // App loading
    if (loading) return <Screens.Loading />;

    if (user && !account) {
      return <Screens.Auth.CreateAccount />;
    }

    const routerProps = { golf, user, account };

    return <Router {...routerProps} />;
  }

  /* */
  render() {
    const { golf } = this.props;

    // Golf loading
    if (golf === null) return <Screens.Loading />;

    if (!golf) return <Screens.Public.NotFound />;

    return (
      <PublicBookingProvider golf={golf}>
        <BrowserRouter basename={`/${this.slug}`}>
          {this.slug && <Manifest golf={golf} />}
          {this.renderRoutes()}
        </BrowserRouter>
      </PublicBookingProvider>
    );
  }
}

const mapStateToProps = ({ app }) => ({ ...app });

export default connect(mapStateToProps, App.actions)(Bootstrap);
